import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, MenuItem, Button, Divider,
  FormControlLabel, Switch
} from '@mui/material';

const weekDays = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export default function PayPeriodSettingsDialog({
  open, onClose, onSave,
  settingsForm, setSettingsForm
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pay Period Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>Pay Period Type</Typography>
            <TextField
              select
              value={settingsForm.period_type}
              onChange={(e) => setSettingsForm({ ...settingsForm, period_type: e.target.value })}
              fullWidth
              size="small"
            >
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="biweekly">Bi-Weekly (Every 2 weeks)</MenuItem>
              <MenuItem value="semimonthly">Semi-Monthly (1st-15th, 16th-end)</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </TextField>
          </Box>

          {(settingsForm.period_type === 'weekly' || settingsForm.period_type === 'biweekly') && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Week Starts On</Typography>
              <TextField
                select
                value={settingsForm.week_start_day}
                onChange={(e) => setSettingsForm({ ...settingsForm, week_start_day: parseInt(e.target.value) })}
                fullWidth
                size="small"
              >
                {weekDays.map((day) => (
                  <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          {settingsForm.period_type === 'biweekly' && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Anchor Date</Typography>
              <TextField
                type="date"
                value={settingsForm.anchor_date}
                onChange={(e) => setSettingsForm({ ...settingsForm, anchor_date: e.target.value })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText="The start date of a known pay period (used to calculate future periods)"
              />
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>Paycheck Day</Typography>
            {(settingsForm.period_type === 'weekly' || settingsForm.period_type === 'biweekly') ? (
              <TextField
                select
                value={settingsForm.paycheck_day ?? ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, paycheck_day: e.target.value === '' ? null : parseInt(e.target.value) })}
                fullWidth
                size="small"
                helperText="Day of the week paychecks are issued"
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Not set</MenuItem>
                {weekDays.map((day) => (
                  <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                select
                value={settingsForm.paycheck_day ?? ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, paycheck_day: e.target.value === '' ? null : parseInt(e.target.value) })}
                fullWidth
                size="small"
                helperText="Day of the month paychecks are issued"
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Not set</MenuItem>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <MenuItem key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}</MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>Auto-Lock Settings</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={settingsForm.auto_lock_enabled}
                  onChange={(e) => setSettingsForm({ ...settingsForm, auto_lock_enabled: e.target.checked })}
                />
              }
              label="Automatically lock periods after they end"
            />
            {settingsForm.auto_lock_enabled && (
              <TextField
                label="Days after period ends"
                type="number"
                value={settingsForm.auto_lock_days_after}
                onChange={(e) => setSettingsForm({ ...settingsForm, auto_lock_days_after: parseInt(e.target.value) || 0 })}
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                inputProps={{ min: 0, max: 14 }}
              />
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Notification Settings</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={settingsForm.notify_before_lock}
                  onChange={(e) => setSettingsForm({ ...settingsForm, notify_before_lock: e.target.checked })}
                />
              }
              label="Notify employees before period locks"
            />
            {settingsForm.notify_before_lock && (
              <TextField
                label="Days before lock"
                type="number"
                value={settingsForm.notify_days_before}
                onChange={(e) => setSettingsForm({ ...settingsForm, notify_days_before: parseInt(e.target.value) || 1 })}
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                inputProps={{ min: 1, max: 7 }}
              />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} variant="contained">Save Settings</Button>
      </DialogActions>
    </Dialog>
  );
}
